import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  Check,
  X,
  Clock,
  Pencil,
  MessageSquare,
  RefreshCw,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { CommonTable } from "../../components/CommonTable";
import { Button } from "../../components/ui/Button";
import { useAppSelector } from "../../store/hooks/reduxHooks";
import {
  ApprovalStatus,
  MerchantApproval,
  fetchMerchantApprovals,
  updateMerchantApproval,
} from "../../services/merchantApproval";
import { showToast } from "../../utils/toast";

type StatusFilter = "ALL" | ApprovalStatus;

const STATUS_STYLES: Record<
  ApprovalStatus,
  { label: string; badge: string; dot: string }
> = {
  PENDING: {
    label: "Action Required",
    badge: "bg-gray-50 text-gray-600 ring-gray-500/15",
    dot: "bg-gray-400",
  },
  UNDER_REVIEW: {
    label: "Under Review",
    badge: "bg-amber-50 text-amber-700 ring-amber-600/20",
    dot: "bg-amber-500",
  },
  APPROVED: {
    label: "Approved",
    badge: "bg-green-50 text-green-700 ring-green-600/20",
    dot: "bg-green-500",
  },
  REJECTED: {
    label: "Rejected",
    badge: "bg-red-50 text-red-700 ring-red-600/20",
    dot: "bg-red-500",
  },
};

// 20000000 -> "2 Cr", 500000 -> "5 L"
const formatRevenue = (value?: number) => {
  if (typeof value !== "number") return "-";
  if (value >= 10000000) return `${+(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `${+(value / 100000).toFixed(2)} L`;
  return value.toLocaleString("en-IN");
};

const MerchantApprovalTab: React.FC = () => {
  // Auth only (token + logged-in user's lender code)
  const { token, user } = useAppSelector((state) => state.auth);
  const lenderCode: string | undefined = (user as any)?.code;

  // Data
  const [approvals, setApprovals] = useState<MerchantApproval[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Table params
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");

  // Search (CommonTable owns the input; the fetch effect below debounces)
  const [searchQuery, setSearchQuery] = useState("");

  // Bump to force a refetch with the same params (refresh button)
  const [refreshKey, setRefreshKey] = useState(0);

  // Row actions
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [remarkModal, setRemarkModal] = useState<{
    id: string;
    remark: string;
  } | null>(null);
  const [isSavingRemark, setIsSavingRemark] = useState(false);

  /**
   * Fetch on mount and whenever params change.
   * `cancelled` drops responses from outdated requests.
   */
  useEffect(() => {
    if (!token || !lenderCode) return;

    let cancelled = false;

    const timeout = setTimeout(async () => {
      try {
        setLoading(true);

        const res = await fetchMerchantApprovals({
          page,
          pageSize,
          sortBy,
          sortOrder,
          search: searchQuery,
          lender: lenderCode,
          status: statusFilter === "ALL" ? undefined : statusFilter,
          token,
        });

        if (cancelled) return;

        setApprovals(res.data);
        setTotal(res.pagination.totalMatch);
      } catch (error: any) {
        if (cancelled) return;
        setApprovals([]);
        setTotal(0);
        showToast.error(error?.message || "Failed to fetch merchant approvals");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [token, lenderCode, page, pageSize, sortBy, sortOrder, searchQuery, statusFilter, refreshKey]);

  // Search
  const handleSearchChange = (value: string) => {
    setPage(1);
    setSearchQuery(value.trim());
  };

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  // Replace one row after a successful PATCH (no refetch needed)
  const replaceRow = (updated: MerchantApproval) =>
    setApprovals((prev) => prev.map((a) => (a._id === updated._id ? updated : a)));

  // Update status
  const handleStatusChange = async (id: string, status: ApprovalStatus) => {
    try {
      setUpdatingId(id);
      const updated = await updateMerchantApproval(id, { status }, token);
      replaceRow(updated);
      showToast.success(`Status updated to ${STATUS_STYLES[status].label}`);
    } catch (error: any) {
      showToast.error(error?.message || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  // Save remark (add / edit / clear)
  const handleSaveRemark = async () => {
    if (!remarkModal) return;

    try {
      setIsSavingRemark(true);
      const updated = await updateMerchantApproval(
        remarkModal.id,
        { remark: remarkModal.remark.trim() },
        token
      );
      replaceRow(updated);
      showToast.success("Remark saved");
      setRemarkModal(null);
    } catch (error: any) {
      showToast.error(error?.message || "Failed to save remark");
    } finally {
      setIsSavingRemark(false);
    }
  };

  const renderStatus = (status: ApprovalStatus) => {
    const s = STATUS_STYLES[status] ?? STATUS_STYLES.PENDING;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ring-1 ring-inset ${s.badge}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
        {s.label}
      </span>
    );
  };

  const columns = useMemo<ColumnDef<MerchantApproval, any>[]>(
    () => [
      {
        id: "srNo",
        header: () => <div className="w-full text-center">Sr. No</div>,
        size: 60,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="w-full text-center">{(page - 1) * pageSize + row.index + 1}</div>
        ),
      },
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
        size: 150,
        cell: ({ row }) => (
          <div className="text-sm font-medium break-words whitespace-normal">
            {row.original.name}
          </div>
        ),
      },
      {
        id: "legalName",
        header: "Legal Name",
        accessorKey: "legalName",
        size: 170,
        cell: ({ row }) => (
          <div className="text-sm break-words whitespace-normal">{row.original.legalName}</div>
        ),
      },
      {
        id: "category",
        header: "Category",
        accessorKey: "category",
        size: 200,
        cell: ({ row }) => (
          <div className="text-sm break-words whitespace-normal">{row.original.category}</div>
        ),
      },
      {
        id: "presence",
        header: "Presence",
        accessorKey: "presence",
        size: 110,
        cell: ({ row }) => <div className="text-sm">{row.original.presence}</div>,
      },
      {
        id: "centers",
        header: "Centres",
        accessorKey: "centers",
        size: 80,
        cell: ({ row }) => (
          <div className="text-sm text-center">{row.original.centers ?? 0}</div>
        ),
      },
      {
        id: "avgAnnualRevenue",
        header: "Avg Annual Revenue",
        accessorKey: "avgAnnualRevenue",
        size: 140,
        cell: ({ row }) => (
          <div className="text-sm font-medium">{formatRevenue(row.original.avgAnnualRevenue)}</div>
        ),
      },
      {
        id: "nabhApproved",
        header: "NABH Approved",
        accessorKey: "nabhApproved",
        size: 110,
        cell: ({ row }) => (
          <div className="text-sm">{row.original.nabhApproved ? "Yes" : "No"}</div>
        ),
      },
      {
        // Status + actions merged
        id: "status",
        header: "Status",
        accessorKey: "status",
        size: 260,
        cell: ({ row }) => {
          const merchant = row.original;
          const busy = updatingId === merchant._id;

          // PENDING -> decision buttons
          if (merchant.status === "PENDING") {
            return (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  className="h-8 px-3 text-xs font-medium text-green-700 border-green-200 hover:bg-green-50 hover:border-green-300"
                  onClick={() => handleStatusChange(merchant._id, "APPROVED")}
                >
                  <Check size={13} className="mr-1.5" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  className="h-8 px-3 text-xs font-medium text-red-700 border-red-200 hover:bg-red-50 hover:border-red-300"
                  onClick={() => handleStatusChange(merchant._id, "REJECTED")}
                >
                  <X size={13} className="mr-1.5" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  className="h-8 px-3 text-xs font-medium text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  onClick={() => handleStatusChange(merchant._id, "UNDER_REVIEW")}
                >
                  <Clock size={13} className="mr-1.5" />
                  Review
                </Button>
              </div>
            );
          }

          // APPROVED / REJECTED / UNDER_REVIEW -> badge, reset, remark
          return (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                {renderStatus(merchant.status)}
                <button
                  type="button"
                  title="Change status"
                  disabled={busy}
                  onClick={() => handleStatusChange(merchant._id, "PENDING")}
                  className="inline-flex items-center justify-center w-6 h-6 text-gray-500 transition-colors rounded-md hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                >
                  <Pencil size={12} />
                </button>
              </div>

              {merchant.remark ? (
                <button
                  type="button"
                  title="Edit remark"
                  onClick={() => setRemarkModal({ id: merchant._id, remark: merchant.remark })}
                  className="flex items-center gap-1.5 text-left max-w-[220px] rounded-md bg-blue-50/60 hover:bg-blue-50 border border-blue-100 px-2 py-1 transition-colors"
                >
                  <MessageSquare size={11} className="text-blue-500 shrink-0" />
                  <span className="text-[11px] leading-tight text-gray-700 truncate">
                    {merchant.remark}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setRemarkModal({ id: merchant._id, remark: "" })}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700 transition-colors w-fit"
                >
                  <MessageSquare size={11} />
                  Add remark
                </button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, updatingId, token]
  );

  return (
    <div className="w-full no-scrollbar">
      {/* Status filter */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <Button
          size="sm"
          variant="outline"
          onClick={handleRefresh}
          disabled={loading}
          className="h-9"
        >
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value as StatusFilter);
            }}
            className="appearance-none min-w-[170px] px-3 py-2 pr-8 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="ALL">All Status</option>
            <option value="PENDING">Action Required</option>
            <option value="UNDER_REVIEW">Under Review</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <ChevronDown
            size={14}
            className="absolute text-gray-500 -translate-y-1/2 pointer-events-none right-2.5 top-1/2"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={approvals}
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPage(1);
            setPageSize(size);
          }}
          onSortChange={(field, order) => {
            setPage(1);
            setSortBy(field);
            setSortOrder(order);
          }}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search merchants..."
          className="merchant-approval-table no-scrollbar"
        />
      </div>

      {/* Remark modal */}
      {remarkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40">
          <div className="w-full max-w-md bg-white shadow-xl rounded-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Merchant Remark</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Add or edit the remark for this approval decision.
                </p>
              </div>
              <button
                type="button"
                disabled={isSavingRemark}
                onClick={() => setRemarkModal(null)}
                className="flex items-center justify-center text-gray-500 rounded-md w-7 h-7 hover:bg-gray-100"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5">
              <textarea
                value={remarkModal.remark}
                onChange={(e) => setRemarkModal({ ...remarkModal, remark: e.target.value })}
                placeholder="Enter remark..."
                rows={4}
                maxLength={500}
                autoFocus
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
              <Button
                size="sm"
                variant="outline"
                disabled={isSavingRemark}
                onClick={() => setRemarkModal(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveRemark}
                isLoading={isSavingRemark}
                disabled={isSavingRemark}
              >
                Save Remark
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .merchant-approval-table td {
            padding: 8px 8px !important;
            vertical-align: middle !important;
          }
          .merchant-approval-table th {
            padding: 7px 8px !important;
            font-size: 0.7rem !important;
          }
        `}
      </style>
    </div>
  );
};

export default MerchantApprovalTab;